/**
 * QuestionCard Component
 * 
 * Zeigt die aktuelle Frage mit allen Optionen,
 * Code-Blöcken, Media und Antwortstatistiken.
 */

import React from 'react';
import { SmartTextRenderer, EnhancedCodeBlock } from '../ui/MathRenderer';
import { QuestionMedia } from '../ui/QuestionMedia';
import TerminalOutput from '../terminalOutput';

/**
 * @param {Object} props
 * @param {Object} props.question - Current question object
 * @param {Object} props.responseStats - { counts, total, percentages }
 * @param {number} props.participantCount - Total participant count
 * @param {boolean} props.showResults - Show result counts
 * @param {boolean} props.showCorrectAnswer - Reveal correct answer
 */
export function QuestionCard({ 
  question,
  responseStats = { counts: {}, total: 0, percentages: {} },
  participantCount = 0,
  showResults = false,
  showCorrectAnswer = false
}) {
  if (!question) return null;

  const { counts, total, percentages } = responseStats;
  const responseRate = participantCount > 0 ? Math.round((total / participantCount) * 100) : 0;

  // Get correct answer indices
  const correctIndices = question.correctAnswerIndices || [0];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-8 mb-6 shadow-lg border border-gray-200 dark:border-gray-700">
      {/* Question Text */}
      <h2 className="text-3xl font-semibold mb-6 leading-relaxed text-gray-900 dark:text-white">
        <SmartTextRenderer text={question.question || question.text} />
      </h2>

      {/* Code Block */}
      {((question.showCodeBlock && question.code) || (question.codeBlock?.code)) && (
        <div className="mb-6">
          <EnhancedCodeBlock 
            code={question.code || question.codeBlock?.code} 
            language={question.codeBlock?.language}
          />
        </div>
      )}

      {/* Media/Image Display */}
      {question.media && (
        <div className="mb-6">
          <QuestionMedia media={question.media} />
        </div>
      )}

      {/* Terminal Output */}
      {((question.showTerminal && question.terminalOutput) || question.terminalOutput) && (
        <div className="mb-6">
          <TerminalOutput output={question.terminalOutput} />
        </div>
      )}

      {/* Response Summary */}
      <div className="mb-4 text-gray-500 dark:text-gray-400">
        Antworten: <span className="text-gray-900 dark:text-white font-semibold">{total}</span> / {participantCount} Teilnehmer
        {participantCount > 0 && (
          <span className="ml-3">({responseRate}%)</span>
        )}
      </div>

      {/* Answer Options */}
      <div className="space-y-3">
        {question.options?.map((option, index) => {
          const isCorrect = correctIndices.includes(index);
          const responseCount = counts[index] || 0;
          const percentage = percentages[index] || 0;

          return (
            <div
              key={index}
              className={`relative rounded-lg p-4 border-2 transition-all ${
                showCorrectAnswer && isCorrect
                  ? 'border-success-500 bg-success-500/20'
                  : showCorrectAnswer && !isCorrect
                  ? 'border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-700/30 opacity-60'
                  : 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50'
              }`}
            >
              {/* Option Label */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className={`text-lg font-bold w-8 h-8 flex items-center justify-center rounded ${
                    showCorrectAnswer && isCorrect 
                      ? 'bg-success-500 text-white' 
                      : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                  }`}>
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span className="text-xl text-gray-900 dark:text-white">
                    <SmartTextRenderer text={option} />
                  </span>
                  {showCorrectAnswer && isCorrect && (
                    <span className="text-success-500 dark:text-success-400 text-2xl">✓</span>
                  )}
                </div>
                {showResults && (
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-gray-500 dark:text-gray-400">{responseCount} Antworten</span>
                    <span className="font-semibold text-lg text-gray-900 dark:text-white">{percentage.toFixed(0)}%</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default QuestionCard;
